/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { MyInjector } from '@src/app/app.module';
import { AbstractObject } from '@src/app/model/abstract-object';
import { RequestOwner } from '@src/app/model/RequestOwner';
import { RequestOwnerService } from '@src/app/modules/crm/request-owner.service';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { Field } from '@src/app/shared/field/Field';
import { PickLinkField } from '@src/app/shared/field/PickLinkField';
import { FormPickLinkComponent } from '@src/app/shared/form/form-pick-link/form-pick-link.component';

/** What a row with an owner looks like, whichever model it came from. */
interface OwnedItem extends AbstractObject {
    ownedById?: string | number;
    ownedByName?: string;
}

/**
 * Who owns a piece of work - a request, or an action on a unit, supplier, contact or fixed asset.
 *
 * One field for all of them, because the question is the same everywhere: the agency's own staff,
 * plus any OMC member with a login, offered as one list that says which side each name sits on.
 * /requestOwners answers it from the tenant alone and knows nothing about requests despite its name.
 *
 * Renders from the flat ownedByName every owner join now selects, so no User record has to travel
 * with the row. In a grid the row must carry FieldMaker.idHolder('teamId') for the picker to resolve
 * its tenant - see PickLinkField.getCurrentItem().
 */
export function makeItemOwnerField(options: Partial<Field> = {}): PickLinkField {
    return FormPickLinkComponent.make({
        label: $localize`Owned By`,
        value: 'ownedById',
        textValue: 'ownedByName',
        nav: ServiceRequestPageNavRoute,
        // No page of their own, so the link goes to what that owner currently has open.
        navFilter: (o: OwnedItem) => ({ ownedById: o.ownedById, srStatus: 'open' }),
        pick: {
            service: MyInjector.instance.get(RequestOwnerService),
            config: () => RequestOwner.pickDialogFormConfig(),
            forceTeamId: (o: AbstractObject) => o.teamId,
        },
    }, options);
}
